import React from 'react';
import { formatDate } from 'utils';
import Card from 'components/Card/Card';
import { useAsync } from 'react-async-hook';
import { providerService } from 'services';
import { Link, useParams } from 'react-router-dom/cjs/react-router-dom';
import { Spin, Typography, Button } from 'antd';
import Table from 'components/Table';
import { ENDASH } from 'constants';
import EmptyState from 'components/EmptyState/EmptyState';
import PageMeta from 'components/PageMeta/PageMeta';
import FormattedContact from 'components/FormattedContact/FormattedContact';
import FormattedAddress from 'components/FormattedAddress/FormattedAddress';

export default function ProviderUserDetails() {
  const { orgId } = useParams();

  // Get organization details
  const { loading: organizationLoading, result: organization = null } = useAsync(async () => {
    if (orgId) {
      return providerService.getOrganization(orgId);
    }
    return null;
  }, [orgId]);

  // Get care facilities for this organization
  const { loading: facilitiesLoading, result: careFacilities = [] } = useAsync(async () => {
    if (orgId && organization) {
      const response = await providerService.getCareFacilities(orgId);
      return response?.content || [];
    }
    return [];
  }, [orgId, organization]);

  if (organization === null) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  // Extract admin info from organization
  const admin = organization?.admin || {};
  const businessContact = organization?.businessContact || {};

  return (
    <>
      <PageMeta title="Provider user details" />

      {organization && (
        <Card title="Provider user details" className="max-w-[600px] mb-12">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex flex-col gap-2">
              <Typography.Text>
                <span className="!font-semibold">Org ID:</span> {orgId ?? ENDASH}
              </Typography.Text>

              <Typography.Text>
                <span className="!font-semibold">Name:</span>{' '}
                {admin?.firstName && admin?.lastName ? `${admin.firstName} ${admin.lastName}` : ENDASH}
              </Typography.Text>

              <Typography.Text>
                <span className="!font-semibold">Username:</span> {admin?.email ?? ENDASH}
              </Typography.Text>

              <Typography.Text>
                <span className="!font-semibold">Created date:</span>{' '}
                {organization?.createdDate ? formatDate(organization.createdDate) : ENDASH}
              </Typography.Text>

              <Typography.Text>
                <span className="!font-semibold">Business Name:</span> {organization?.businessLegalName ?? ENDASH}
              </Typography.Text>
            </div>

            <div className="flex flex-col gap-2">
              <Typography.Text>
                <span className="!font-semibold">Business Address:</span>{' '}
                <FormattedAddress address={organization?.address} />
              </Typography.Text>

              <Typography.Text>
                <span className="!font-semibold">Organization Contact:</span>{' '}
                <FormattedContact contact={businessContact} />
              </Typography.Text>
            </div>
          </div>
        </Card>
      )}

      {facilitiesLoading ? (
        <Card title="Loading care facilities...">
          <Spin />
        </Card>
      ) : careFacilities && careFacilities.length > 0 ? (
        <Card
          noBodyPadding
          title={`${careFacilities.length} provider location${careFacilities.length !== 1 ? 's' : ''}`}
        >
          <Table
            dataSource={careFacilities}
            columns={[
              {
                title: 'Location ID',
                dataIndex: 'id',
                render: (id) => id ?? ENDASH,
              },
              {
                title: 'Location name',
                dataIndex: 'businessLegalName',
                render: (name) => name ?? ENDASH,
              },
              {
                title: 'Location Address',
                dataIndex: 'address',
                render: (address) => {
                  if (!address) return ENDASH;
                  return `${address.street || ''}${address.street2 ? ', ' + address.street2 : ''}, ${
                    address.city || ''
                  }, ${address.state || ''} ${address.zip || ''}`.trim();
                },
              },
              {
                title: 'City',
                dataIndex: 'address',
                render: (address) => address?.city ?? ENDASH,
              },
              {
                title: 'State',
                dataIndex: 'address',
                render: (address) => address?.state ?? ENDASH,
              },
              {
                title: 'Zip',
                dataIndex: 'address',
                render: (address) => address?.postalCode ?? ENDASH,
              },
              {
                title: 'Tax ID',
                dataIndex: 'taxIdNumber',
                render: (taxId) => taxId ?? ENDASH,
              },
              {
                title: 'Location Type',
                dataIndex: 'type',
                render: (type) => type?.title ?? ENDASH,
              },
              {
                title: 'Created Date',
                dataIndex: 'createdDate',
                render: (date) => {
                  return date ? formatDate(date) : ENDASH;
                },
              },
              {
                title: 'Actions',
                dataIndex: 'id',
                fixed: 'right',
                width: 120,
                render: (id, record) => (
                  <Link to={`/providers/admin/org/${orgId}/facility/${id}`}>
                    <Button type="primary">View</Button>
                  </Link>
                ),
              },
            ]}
            childrenColumnName="_"
            loading={facilitiesLoading || organizationLoading}
          />
        </Card>
      ) : (
        <Card>
          <EmptyState description="No provider locations found for this organization" />
        </Card>
      )}
    </>
  );
}
